import { useRouter } from "next/router";
import { useCategoryItemContext } from "../../../../../../services/context/CategoryItemContext";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../services/hooks/useReduxHooks";
import {
  setBottomSheetSource,
  setBottomSheetState,
  setScrollYOfCategoryScreen,
} from "../../../../../../services/redux/slices/globalComponentsToggleSlice";
import { setSelectedItemTimeBoundData } from "../../../../../../services/redux/slices/itemStatusSlice";
import { PagesEnum } from "../../../../../../utils/enums";
import { getPageLink } from "../../../../../../utils/help/navigation/navigation";
import { PLink } from "../../../../../global/PLink";
import { backToPreviousPosition } from "../../CategoryItemListAnimation";
import { CategoryItemCardV2Grid } from "./CategoryItemCardV2Grid";
import { CategoryItemCardV2List } from "./CategoryItemCardV2List";

export const CategoryItemCardV2 = () => {
  const { item, isMenuView } = useCategoryItemContext();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const shouldOpenBottomSheet = useAppSelector((state) => {
    const restaurantInfo = state.config.restaurantInfo;
    const bottomSheetModal = state.globalComponentsToggleSlice.bottomSheetModal;
    const selectedFranchise = state.config.selectedFranchise;
    const orderTypeParam = state.queryParams.orderTypeParam;

    return Boolean(
      restaurantInfo &&
        !bottomSheetModal.isOpen &&
        (!selectedFranchise || !orderTypeParam)
    );
  });

  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );

  const CardContent = isMenuView
    ? CategoryItemCardV2Grid
    : CategoryItemCardV2List;

  const itemLink = getPageLink(router, PagesEnum.Item, {
    as: item.id + "",
  });

  /**
   * Give the <Link> an href whose query matches the one next builds for the
   * NAVIGATION, so the prefetch and the navigation request the same url.
   *
   * getPageLink already returns the route pattern as href
   * ("/item/[item]?<params>", since PagesEnum.Item is "item/[item]") and the
   * resolved url as `as`. What differs is the QUERY: next derives the data url
   * from the href's query (page-loader getDataHref appends `search`), and on
   * navigation it rebuilds that href itself from `{ pathname, query }` --
   * where query already carries the interpolated route param. So navigation
   * asks for
   *
   *   /_next/data/<build>/en/item/23080.json?franchise=..&order_type=..&item=23080
   *
   * while a plain href only produced
   *
   *   /_next/data/<build>/en/item/23080.json?franchise=..&order_type=..
   *
   * The cache key is the whole url string (router fetchNextData: `new
   * URL(dataHref).href`), so those are two entries and the prefetched copy was
   * never the one the navigation read -- it refetched the full body every
   * time. Appending the route param LAST reproduces next's own ordering: the
   * page's existing query first, then the interpolated param.
   */
  const itemHref = (() => {
    const [, search = ""] = (itemLink.as ?? "").split("?");
    const params = search ? `${search}&item=${item.id}` : `item=${item.id}`;

    return `/${PagesEnum.Item}?${params}`;
  })();

  if (isMobile) {
    if (!shouldOpenBottomSheet) {
      return (
        <PLink
          // `as` keeps the address bar on /item/<id>; href carries the route
          // pattern so next interpolates it to the same data url the
          // navigation builds. See itemHref above.
          link={{ href: itemHref, as: itemLink.as }}
          onClick={() =>
            dispatch(
              setSelectedItemTimeBoundData(item.timeBoundAvailabilityData)
            )
          }
          passHref
        >
          <a
            onClick={() => {
              backToPreviousPosition((p: number) =>
                dispatch(setScrollYOfCategoryScreen(p))
              );
            }}
          >
            <CardContent />
          </a>
        </PLink>
      );
    }

    return (
      <a
        onClick={() => {
          dispatch(setBottomSheetSource("item_selection"));
          dispatch(setBottomSheetState(true));
        }}
      >
        <CardContent />
      </a>
    );
  }

  return <CardContent />;
};
